import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from 'react-query'
import { rolesAPI } from '../../services/api'
import productRolesAPI from '../../services/productRolesAPI'
import { productsAPI } from '../../services/productsAPI'
import Card, { CardHeader, CardTitle, CardContent } from '../UI/Card'
import Button from '../UI/Button'
import LoadingSpinner from '../UI/LoadingSpinner'
import { X, Shield, Plus } from 'lucide-react'
import toast from 'react-hot-toast'

export default function CreateRoleModal({ isOpen, onClose, productId }) {
  const queryClient = useQueryClient()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [availablePermissions, setAvailablePermissions] = useState({})
  const [features, setFeatures] = useState([])
  const [modules, setModules] = useState([])
  const [expandedModules, setExpandedModules] = useState(new Set())

  const { register, handleSubmit, reset, formState: { errors }, watch } = useForm({
    defaultValues: {
      name: '',
      description: '',
      permissions: {}
    }
  })

  // Fetch available permissions and modules when modal opens
  useEffect(() => {
    console.log('CreateRoleModal useEffect - isOpen:', isOpen, 'productId:', productId)
    if (isOpen && productId) {
      // Fetch features
      productsAPI.getFeatures(productId)
        .then(response => {
          console.log('Features API response:', response)
          const features = response.data?.data?.features || []
          console.log('Features found:', features)
          setFeatures(features)
        })
        .catch(error => {
          console.error('Error fetching features:', error)
        })

      // Fetch permissions (for role creation) - use product roles API for permissions
      productRolesAPI.getAvailablePermissions(productId)
        .then(response => {
          const permissions = response.data.permissions || {}
          setAvailablePermissions(permissions)
        })
        .catch(error => {
          console.error('Error fetching permissions:', error)
        })

      // Fetch modules
      productsAPI.getModules(productId)
        .then(response => {
          console.log('Modules API response:', response)
          const modules = response.data?.data?.modules || []
          console.log('Modules found:', modules)
          setModules(modules)
        })
        .catch(error => {
          console.error('Error fetching modules:', error)
        })
    }
  }, [isOpen, productId])

  const mutation = useMutation((roleData) => rolesAPI.create(roleData), {
    onSuccess: () => {
      queryClient.invalidateQueries(['product-roles', productId])
      queryClient.invalidateQueries(['product', productId])
      queryClient.invalidateQueries(['roles'])
      toast.success('Role created successfully!')
      reset()
      onClose()
    },
    onError: (error) => {
      console.error('Error creating role:', error)
      toast.error(error.message || 'Failed to create role')
    }
  })

  const onSubmit = async (data) => {
    setIsSubmitting(true)
    try {
      const roleData = {
        name: data.name,
        description: data.description,
        permissions: data.permissions,
        user_type: 'organization',
        product_id: productId // Include product_id for tracking
      }
      console.log('Creating role with data:', roleData)
      await mutation.mutateAsync(roleData)
    } catch (error) {
      // Error handled in mutation onError
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      reset()
      onClose()
    }
  }

  const handlePermissionChange = (category, permission, checked) => {
    const currentPermissions = watch('permissions') || {}
    const updatedPermissions = {
      ...currentPermissions,
      [category]: {
        ...currentPermissions[category],
        [permission]: checked
      }
    }
    reset({
      ...watch(),
      permissions: updatedPermissions
    })
  }

  const handleFeaturePermissionChange = (featureId, checked) => {
    const currentPermissions = watch('permissions') || {}
    const updatedPermissions = {
      ...currentPermissions,
      features: {
        ...currentPermissions.features,
        [featureId]: checked
      }
    }
    reset({
      ...watch(),
      permissions: updatedPermissions
    })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div className="flex items-center space-x-2">
            <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <Shield className="h-4 w-4 text-blue-600" />
            </div>
            <CardTitle>Create Feature Role</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Role Name */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Feature Role Name *
              </label>
              <input
                {...register('name', { 
                  required: 'Role name is required',
                  minLength: { value: 2, message: 'Name must be at least 2 characters' }
                })}
                type="text"
                className="input w-full"
                placeholder="e.g., Feature Manager, Basic User, Premium User"
                disabled={isSubmitting}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Description
              </label>
              <textarea
                {...register('description')}
                rows={3}
                className="input w-full resize-none"
                placeholder="Describe what features this role can access and manage"
                disabled={isSubmitting}
              />
            </div>

            {/* Feature Permissions */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-4">
                Feature Access Permissions
              </label>
              
              {modules.filter(module => {
                const moduleFeatures = features.filter(feature => feature.module_id === module.id)
                return moduleFeatures.length > 0
              }).length > 0 ? (
                <div className="space-y-2">
                  {/* Tree-like structure with modules and their features */}
                  {modules
                    .filter(module => {
                      const moduleFeatures = features.filter(feature => feature.module_id === module.id)
                      return moduleFeatures.length > 0
                    })
                    .map((module) => {
                    const moduleFeatures = features.filter(feature => feature.module_id === module.id)
                    const isExpanded = expandedModules.has(module.id)
                    
                    return (
                      <div key={module.id} className="border border-secondary-200 rounded-lg">
                        {/* Module Header */}
                        <div className="p-3">
                          <div 
                            className="flex items-center justify-between cursor-pointer hover:bg-secondary-50"
                            onClick={() => {
                              const newExpanded = new Set(expandedModules)
                              if (isExpanded) {
                                newExpanded.delete(module.id)
                              } else {
                                newExpanded.add(module.id)
                              }
                              setExpandedModules(newExpanded)
                            }}
                          >
                          <div className="flex items-center space-x-3">
                            <div className="flex items-center">
                              {isExpanded ? (
                                <svg className="h-4 w-4 text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              ) : (
                                <svg className="h-4 w-4 text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              )}
                            </div>
                            <div>
                              <h4 className="font-medium text-secondary-900">{module.name}</h4>
                              <p className="text-sm text-secondary-600">
                                {moduleFeatures.length} feature{moduleFeatures.length !== 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              module.type === 'required' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {module.type === 'required' ? 'Required' : 'Optional'}
                            </span>
                            {module.price > 0 && (
                              <span className="text-sm font-medium text-secondary-900">
                                ${module.price}
                              </span>
                            )}
                          </div>
                          </div>
                        </div>
                        
                        {/* Module Features (Collapsible) */}
                        {isExpanded && (
                          <div className="border-t border-secondary-200 p-3 bg-secondary-50">
                            {/* Module Select All Checkbox */}
                            <div className="mb-3 pb-2 border-b border-secondary-200">
                              <label className="flex items-center space-x-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={moduleFeatures.length > 0 && moduleFeatures.every(feature => 
                                    watch(`permissions.features.${feature.id}`) || false
                                  )}
                                  onChange={(e) => {
                                    const isChecked = e.target.checked
                                    moduleFeatures.forEach(feature => {
                                      handleFeaturePermissionChange(feature.id, isChecked)
                                    })
                                  }}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                  disabled={isSubmitting}
                                />
                                <span className="text-sm font-medium text-secondary-700">
                                  Select All
                                </span>
                              </label>
                            </div>
                            
                            {moduleFeatures.length > 0 ? (
                              <div className="space-y-2">
                                {moduleFeatures.map((feature) => (
                                  <label key={feature.id} className="flex items-start space-x-3">
                                    <input
                                      type="checkbox"
                                      checked={watch(`permissions.features.${feature.id}`) || false}
                                      onChange={(e) => handleFeaturePermissionChange(feature.id, e.target.checked)}
                                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                                      disabled={isSubmitting}
                                    />
                                    <div className="flex-1">
                                      <span className="text-sm font-medium text-secondary-900">{feature.name}</span>
                                      {feature.description && (
                                        <p className="text-xs text-secondary-600 mt-1">{feature.description}</p>
                                      )}
                                    </div>
                                  </label>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-4">
                                <p className="text-sm text-secondary-600">No features available for this module</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="text-center py-8 border border-secondary-200 rounded-lg">
                  <div className="text-secondary-400 mb-2">
                    <svg className="h-12 w-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-secondary-900 mb-2">No Modules with Features Available</h3>
                  <p className="text-secondary-600 mb-4">Create modules and add features to them in the Modules and Features tabs first, then assign permissions to their features here.</p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-secondary-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="min-w-[100px]"
              >
                {isSubmitting ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  'Create Role'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
